<template>
  <div class="language-switch" ref="languageSwitch">
    <!-- 语言切换按钮 -->
    <div
      class="language-button"
      @click="toggleDropdown"
      :class="{ active: isDropdownOpen }"
    >
      <div class="language-button-left">
        <img class="globe-icon" src="@/assets/img/pc/round.png" />
        <span class="current-language">{{ currentLanguageName }}</span>
      </div>
      <img src="@/assets/img/pc/pull.png" class="pull-icon" />
      <!-- <span class="arrow-icon" :class="{ rotated: isDropdownOpen }">▼</span> -->
    </div>

    <!-- 语言选择下拉菜单 -->
    <div
      v-if="isDropdownOpen"
      class="language-dropdown"
      :class="{ rtl: currentLocale === 'ar' }"
    >
      <div
        v-for="locale in supportedLocales"
        :key="locale.code"
        class="language-option"
        :class="{ active: locale.code === currentLocale }"
        @click="selectLanguage(locale.code)"
      >
        <!-- <span class="flag">{{ locale.flag }}</span> -->
        <span class="name">{{ locale.name }}</span>
        <!-- <span v-if="locale.code === currentLocale" class="check">✓</span> -->
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { supportedLocales } from '@/i18n';
import { px2rem } from '@/utils/px2rem';

export default {
  name: 'LanguageSwitch',
  setup() {
    const { locale } = useI18n();
    const isDropdownOpen = ref(false);
    const languageSwitch = ref(null);

    // 当前语言信息
    const currentLocale = computed(() => locale.value);
    const currentLanguageName = computed(() => {
      const current = supportedLocales.find(
        (l) => l.code === currentLocale.value
      );
      return current ? current.name : '中文';
    });

    // 切换下拉菜单
    const toggleDropdown = () => {
      isDropdownOpen.value = !isDropdownOpen.value;
    };

    // 选择语言
    const selectLanguage = (langCode) => {
      locale.value = langCode;
      localStorage.setItem('locale', langCode);
      isDropdownOpen.value = false;

      // 设置文档方向（阿拉伯语从右到左）
      if (langCode === 'ar') {
        document.documentElement.dir = 'rtl';
        document.documentElement.lang = 'ar';
      } else {
        document.documentElement.dir = 'ltr';
        document.documentElement.lang = langCode;
      }
    };

    // 点击外部关闭下拉菜单
    const handleClickOutside = (event) => {
      if (
        languageSwitch.value &&
        !languageSwitch.value.contains(event.target)
      ) {
        isDropdownOpen.value = false;
      }
    };

    // 键盘事件处理
    const handleKeydown = (event) => {
      if (event.key === 'Escape') {
        isDropdownOpen.value = false;
      }
    };

    onMounted(() => {
      document.addEventListener('click', handleClickOutside);
      document.addEventListener('keydown', handleKeydown);

      // 初始化文档方向
      if (currentLocale.value === 'ar') {
        document.documentElement.dir = 'rtl';
        document.documentElement.lang = 'ar';
      }
    });

    onUnmounted(() => {
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('keydown', handleKeydown);
    });

    return {
      isDropdownOpen,
      languageSwitch,
      currentLocale,
      currentLanguageName,
      supportedLocales,
      toggleDropdown,
      selectLanguage,
    };
  },
};
</script>

<style lang="scss" scoped>
.language-switch {
  position: relative;
  display: inline-block;
  width: 116px;
  height: 32px;
}

.language-button {
  display: flex;
  align-items: center;
  justify-content: space-between;
  // gap: 8px;
  // padding: 7px 14px;
  background: #ffffff;
  border: 1px solid #e1e5e9;
  border-radius: 20px;
  cursor: pointer;
  font-size: 14px;
  height: 100%;
  color: #333;
  transition: all 0.3s ease;
  border: 1px solid #aaaab3;
  &:hover {
    background: #e4e9f0;
  }
  .language-button-left {
    display: flex;
    align-items: center;
    margin-left: 8px;
    justify-content: center;
    white-space: nowrap;
  }
}

.globe-icon {
  width: 18px;
  height: 18px;
  margin-right: 5px;
}
.pull-icon {
  width: 12px;
  margin-right: 8px;
  height: 7px;
  margin-left: 5px;
}

.current-language {
  font-weight: 400;
  font-size: 14px;
  margin-top: 2px;
  color: #3d3d40;
}

.arrow-icon {
  font-size: 12px;
  transition: transform 0.3s ease;

  &.rotated {
    transform: rotate(180deg);
  }
}

.language-dropdown {
  position: absolute;
  top: 100%;
  left: 0; /* 改为左对齐 */
  margin-top: 8px;
  padding: 12px;
  background: white;
  border: 1px solid #e4e7ed;
  border-radius: 14px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  min-width: 154px;
  z-index: 1000;
  overflow: hidden;

  &.rtl {
    left: auto; /* RTL 语言时改为右对齐 */
    right: 0;
  }
}

.language-option {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  margin-bottom: 2px;
  &:hover {
    background: #f5f7fa;
    border-radius: 8px;
  }

  &.active {
    background: #f5f7fa;
    border-radius: 8px;
  }

  &:not(:last-child) {
    // border-bottom: 1px solid #f0f0f0;
  }
}

.flag {
  font-size: 18px;
  width: 24px;
  text-align: center;
}

.name {
  flex: 1;
  color: #1a1a1a;
}

.check {
  color: #4caf50;
  font-weight: bold;
  font-size: 16px;
}

// 响应式设计
@media (max-width: 768px) {
  .language-button {
    padding: 6px 12px;
    font-size: 13px;
  }

  .language-dropdown {
    min-width: 140px;
  }

  .language-option {
    padding: 10px 12px;
  }
}

// RTL 支持
[dir='rtl'] .language-button {
  flex-direction: row-reverse;
}

[dir='rtl'] .language-option {
  flex-direction: row-reverse;
}
</style>
